import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Box, Skeleton } from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import styled from 'styled-components';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LabelList } from 'recharts';
import { useDispatch } from 'react-redux';
import { useOragnization } from 'components/hooks/oragnization/useOragnization';
import { handleGetDomainPerformanceGraphData } from 'store/standardexams';
import ContentLoading from 'modules/standardExamReport/contentLoading';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import dayjs from 'utils/dayjsConfig';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Button } from '@mui/material';

const EXAM_TYPE_LABEL = ['Reading', 'Speaking', 'Listening', 'Writing'];

const ChartWrap = styled.div`
  width: 100%;
  height: 100%;
  min-height: 300px;
  flex: 1;
`;

const SkeletonContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
`;

const SkeletonBarsWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
  height: calc(100% - 50px);
  gap: 4px;
`;

const SkeletonBar = styled(Skeleton)`
  flex: 1;
  max-width: 80px;
  border-radius: 8px 8px 0 0;
`;

const DomainPerformanceGraph = ({
  filterRecords: initialFilterRecords,
  optionsData,
  activeSession,
  exams = [],
  currentUser = null,
  currentClass = null,
}) => {
  const { currentOrg } = useOragnization();
  const dispatch = useDispatch();
  const showAlert = useAlert();
  const [loading, setLoading] = useState(false);
  const [localRows, setLocalRows] = useState([]);
  const [filterRecords, setFilterRecords] = useState({
    exam_type: initialFilterRecords?.exam_type || '1',
    school_level: initialFilterRecords?.school_level || 'high',
  });
  const lastPayloadRef = useRef(null);
  const [maxMenuWidth, setMaxMenuWidth] = useState(150);
  const examTypeOptions = useMemo(() => {
    if (!Array.isArray(exams) || !activeSession) return [];
    const selectedSchoolExam = exams.find(
      (exam) => exam.exam_school === filterRecords.school_level && exam.session === activeSession
    );
    if (!selectedSchoolExam?.exams?.length) return [];
    return selectedSchoolExam.exams.map((exam) => ({
      value: String(exam.id),
      label: exam.displayName,
      bookId: exam.bookId,
    }));
  }, [exams, activeSession, filterRecords.school_level]);
 
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width > 600 && width < 1280) {
        setMaxMenuWidth(320);
      } else {
        setMaxMenuWidth(120);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const filterExamSession = exams.find(
      (exam) =>
        exam.exam_school === filterRecords.school_level &&
        exam.session === activeSession,
    );
    if(filterExamSession&&filterExamSession.exams){
      const has_id = filterExamSession.exams.find(
        (exam) =>
          exam.id == filterRecords.exam_type,
      );
      if(!has_id && filterRecords.exam_type != '1'){
        setFilterRecords({
          ...filterRecords,
          exam_type: 1,
        });
    }
  }
  }, [activeSession,filterRecords,exams]);

  useEffect(() => {
    if (initialFilterRecords?.exam_type && initialFilterRecords?.school_level) {
      setFilterRecords({
        exam_type: initialFilterRecords.exam_type,
        school_level: initialFilterRecords.school_level,
      });
    }
  }, [initialFilterRecords?.exam_type, initialFilterRecords?.school_level]);

  const fetchData = useCallback(async () => {
    if (!currentOrg?.id || !filterRecords.exam_type || !filterRecords.school_level) return;
    if (!activeSession) return;

    const selectedExam = examTypeOptions.find(
      (item) => String(item.value) === String(filterRecords.exam_type)
    );
    const bookIDs = selectedExam?.bookId;

    const payload = {
      org_id: currentUser?.role == 'teacher' ? currentOrg?.parentId : currentOrg?.id,
      session: activeSession,
      school_type: filterRecords.school_level,
      book_ids: bookIDs ? [bookIDs] : [],
    };

    if (currentUser?.role == 'teacher') {
      payload.teacher_id = currentUser?.id;
      payload.class_id = currentClass?.id;
    }

    const payloadKey = JSON.stringify(payload);
    if (lastPayloadRef.current === payloadKey) return;

    try {
      setLoading(true);
      lastPayloadRef.current = payloadKey;
      const result = await dispatch(handleGetDomainPerformanceGraphData(payload));
      if (result?.status_code === 404) {
        showAlert('No submissions data found', 'error');
      }
      if (result?.data) {
        setLocalRows(result.data);
      }
    } catch (error) {
      console.log(error, 'error in fetching domain performance graph data');
      lastPayloadRef.current = null;
    } finally {
      setLoading(false);
    }
  }, [currentOrg?.id, activeSession, filterRecords.exam_type, filterRecords.school_level, currentClass?.id, currentUser?.role, currentUser?.id, dispatch, showAlert, examTypeOptions]);

  useEffect(() => {
    lastPayloadRef.current = null;
  }, [currentClass?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData,filterRecords]);



  const transformData = useMemo(() => {
    const data = localRows || [];
    if (data && data.result && Array.isArray(data.result) && data.result.length > 0) {
      return data.result.map((item) => ({
        name: item.type,
        value: Math.round(Number(item.count) || 0),
      }));
    }
    if (Array.isArray(data) && data.length > 0) {
      return data.map((item) => ({
        ...item,
        value: Math.round(Number(item.value) || 0),
      }));
    }
    return EXAM_TYPE_LABEL.map((label) => ({
      name: label,
      value: 0,
    }));
  }, [localRows]);

  const chartData = transformData;

  const downloadDomainPerformanceCSV = async () => {
    try {
      if (!localRows || (Array.isArray(localRows) && localRows.length === 0) || (typeof localRows === 'object' && Object.keys(localRows).length === 0)) {
        showAlert('No data available for download', 'warning');
        return;
      }

      const schoolLevelLabel = optionsData?.school_level?.find((item) => item.value === filterRecords.school_level)?.label || filterRecords.school_level || '-';
      const examTypeLabel = examTypeOptions.find((item) => item.value === filterRecords.exam_type)?.label || filterRecords.exam_type || '-';

      let dataArray = [];
      if (localRows && localRows.result && Array.isArray(localRows.result) && localRows.result.length > 0) {
        dataArray = localRows.result.map((item) => ({
          'School Level': schoolLevelLabel,
          'Exam Type': examTypeLabel,
          'Domain Type': item.type || '-',
          'Count': item.count || 0,
        }));
      } else if (Array.isArray(localRows) && localRows.length > 0) {
        dataArray = localRows.map((item) => ({
          'School Level': schoolLevelLabel,
          'Exam Type': examTypeLabel,
          'Domain Type': item.type || item.name || '-',
          'Count': item.count || item.value || 0,
        }));
      } else if (chartData && chartData.length > 0) {
        dataArray = chartData.map((item) => ({
          'School Level': schoolLevelLabel,
          'Exam Type': examTypeLabel,
          'Domain Type': item.name || '-',
          'Count': item.value || 0,
        }));
      }

      if (dataArray.length === 0) {
        showAlert('No data available for download', 'warning');
        return;
      }

      const ExcelJS = (await import('exceljs')).default;
      const { saveAs } = await import('file-saver');
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Domain Performance', {
        views: [{ state: 'frozen', ySplit: 1 }],
      });

      const excelBorderThin = {
        top: { style: 'thin', color: { argb: 'FFB0BEC5' } },
        bottom: { style: 'thin', color: { argb: 'FFB0BEC5' } },
        left: { style: 'thin', color: { argb: 'FFB0BEC5' } },
        right: { style: 'thin', color: { argb: 'FFB0BEC5' } },
      };

      const domainArgbMap = {
        Reading: 'FF3871C1',
        Speaking: 'FFFB742B',
        Listening: 'FF8C52FF',
        Writing: 'FF00A86B',
      };

      sheet.columns = [{ width: 20 }, { width: 34 }, { width: 20 }, { width: 14 }];

      const headerRow = sheet.addRow([
        'School Level',
        'Exam Type',
        'Domain Type',
        'Count',
      ]);
      headerRow.height = 22;
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF02796D' },
        };
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 12 };
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        cell.border = excelBorderThin;
      });

      dataArray.forEach((item, dataIdx) => {
        const row = sheet.addRow([
          item['School Level'] || '-',
          item['Exam Type'] || '-',
          item['Domain Type'] || '-',
          item.Count ?? 0,
        ]);
        row.height = 20;
        const rowFill = dataIdx % 2 === 0 ? 'FFFFFFFF' : 'FFF5F5F5';
        row.eachCell((cell, colNumber) => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: rowFill },
          };
          cell.font = { size: 11, color: { argb: 'FF263238' } };
          cell.border = excelBorderThin;
          cell.alignment = { vertical: 'middle', wrapText: true };
          if (colNumber === 3) {
            const domainName = String(item['Domain Type'] || '').trim();
            const domainColor = domainArgbMap[domainName] || 'FF263238';
            cell.font = { bold: true, size: 11, color: { argb: domainColor } };
          }
          if (colNumber === 4) {
            cell.alignment = { horizontal: 'right', vertical: 'middle' };
          }
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        `domain_performance_${dayjs().format('DD-MM-YY_hh-mm-ss')}.xlsx`
      );
    } catch (error) {
      showAlert('Error downloading report', 'error');
    }
  };

  const handleSchoolLevelChange = async (value) => {
    const selectedSchoolExam = exams.find(
      (exam) => exam.exam_school === value && exam.session === activeSession
    );
    const nextExam = selectedSchoolExam?.exams?.[0];
    const newFilter = {
      exam_type: nextExam ? String(nextExam.id) : '',
      school_level: value,
    };
    
    setFilterRecords(newFilter);
    
    try {
      setLoading(true);
      lastPayloadRef.current = null;
      const bookIDs = nextExam?.bookId;

      const payload = {
        org_id: currentUser?.role == 'teacher' ? currentOrg?.parentId : currentOrg?.id,
        session: activeSession,
        school_type: value,
        book_ids: bookIDs ? [bookIDs] : [],
      };
      if (currentUser?.role == 'teacher') {
        payload.teacher_id = currentUser?.id;
        payload.class_id = currentClass?.id;
      }
      console.log(payload, 'payload 3');

      const result = await dispatch(handleGetDomainPerformanceGraphData(payload));
      if (result?.status_code === 404) {
        showAlert('No submissions data found', 'error');
      }
      if (result?.data) {
        setLocalRows(result.data);
      }
    } catch (error) {
      console.log(error, 'error in handling school level change');
    } finally {
      setLoading(false);
    }
  };

  const handleExamTypeChange = async (value) => {
    const newFilter = {
      exam_type: value,
      school_level: filterRecords.school_level,
    };
    
    setFilterRecords(newFilter);
    
    try {
      setLoading(true);
      lastPayloadRef.current = null;
      const selectedExam = examTypeOptions.find((item) => String(item.value) === String(value));
      const bookIDs = selectedExam?.bookId;

      const payload = {
        org_id: currentUser?.role == 'teacher' ? currentOrg?.parentId : currentOrg?.id,
        session: activeSession,
        school_type: filterRecords.school_level,
        book_ids: bookIDs ? [bookIDs] : [],
      };
      if (currentUser?.role == 'teacher') {
        payload.teacher_id = currentUser?.id;
        payload.class_id = currentClass?.id;
      }
      console.log(payload, 'payload 4');
      const result = await dispatch(handleGetDomainPerformanceGraphData(payload));
      if (result?.status_code === 404) {
        showAlert('No submissions data found', 'error');
      }
      if (result?.data) {
        setLocalRows(result.data);
      }
    } catch (error) {
      console.log(error, 'error in handling exam type change');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* <ContentLoading isLoading={loading} /> */}
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          margin: 0,
        }}
      >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          width: '100%',
          height: '100%',
          gap: 1,
          flex: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            gap: 1,
            mb: 0.5,
          }}
        >
          <p style={{ color: '#000', fontSize: 24, fontWeight: 700, margin: 0, lineHeight: '28px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'normal', wordBreak: 'break-word'  , width:'100%'}}>
            <span style={{ display: 'inline' }}>Domain</span>
            <span style={{ display: 'inline' }}> Performance</span>
          </p>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 0.5, width:'100%', marginRight:'20px'}}>
          <Button
            onClick={downloadDomainPerformanceCSV}
            size="small"
            sx={{
              minWidth: 'auto',
              width: 28,
              height: 28,
              padding: 0,
              borderRadius: '12px',
              backgroundColor: '#EDEDED',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': { 
                backgroundColor: '#E0E0E0',
              },
              '&:active': {
                backgroundColor: '#D5D5D5',
              },
            }}
          >
            <FileDownloadIcon sx={{ fontSize: 18, color: '#61677E' }} />
          </Button>
          <FormControl
            size="small"
            sx={{
              minWidth: 100,
              maxWidth: 130,
              '& .MuiOutlinedInput-root': {
                height: 28,
                borderRadius: '12px',
                bgcolor: '#EDEDED',
                paddingRight: '18px',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
              },
              '& .MuiSelect-select': { py: 0, px: 1, fontSize: 12, lineHeight: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' },
              '& .MuiSelect-icon': { right: 6, fontSize: 18 },
            }}
          >
            <Select
              value={filterRecords.school_level}
              displayEmpty
              IconComponent={KeyboardArrowDownRoundedIcon}
              onChange={(e) => handleSchoolLevelChange(e.target.value)}
              renderValue={(val) => {
                const findValue = optionsData?.school_level?.find((item) => item.value === val);
                return findValue ? findValue.label : 'SCHOOL';
              }}
              inputProps={{ 'aria-label': 'School' }}
              getPopupContainer={(trigger) => trigger.parentNode}
              MenuProps={{
                disableScrollLock: true,
                PaperProps: {
                  sx: {
                    '& .MuiMenuItem-root': {
                      '&:hover': {
                        backgroundColor: '#333333',
                        color: '#ffffff',
                      },
                    },
                    '& .MuiMenuItem-root.Mui-selected': {
                        backgroundColor: 'transparent',
                        '&:hover': {
                          backgroundColor: '#333333',
                          color: '#ffffff',
  
                        },
                      },
                  },
                },
              }}
            >
              {optionsData?.school_level?.map((item) => (
                <MenuItem value={item.value} key={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            size="small"
            sx={{
              minWidth: 88,
              maxWidth: maxMenuWidth,
              '& .MuiOutlinedInput-root': {
                height: 28,
                borderRadius: '12px',
                bgcolor: '#EDEDED',
                paddingRight: '18px',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
              },
              '& .MuiSelect-select': { py: 0, px: 1, fontSize: 12, lineHeight: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' },
              '& .MuiSelect-icon': { right: 6, fontSize: 18 },
            }}
          >
            <Select
              value={filterRecords.exam_type}
              displayEmpty
              IconComponent={KeyboardArrowDownRoundedIcon}
              onChange={(e) => handleExamTypeChange(e.target.value)}
              renderValue={(val) => {
                const findValue = examTypeOptions.find((item) => item.value === val);
                return findValue ? findValue.label : 'EXAM';
              }}
              inputProps={{ 'aria-label': 'Exam' }}
              getPopupContainer={(trigger) => trigger.parentNode}
              MenuProps={{
              disableScrollLock: true,
                PaperProps: {
                  sx: {
                    '& .MuiMenuItem-root': {
                      whiteSpace: 'wrap',
                      '&:hover': {
                        backgroundColor: '#333333',
                        color: '#ffffff',
                      },
                    },
                    '& .MuiMenuItem-root.Mui-selected': {
                        backgroundColor: 'transparent',
                        '&:hover': {
                          backgroundColor: '#333333',
                          color: '#ffffff',
  
                        },
                      },
                  },
                },
              }}
            >
              {examTypeOptions.map((item) => (
                <MenuItem value={item.value} key={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          </Box>
        </Box>
        <ChartWrap>
          {(loading) ? (
            <SkeletonContainer>
              <SkeletonBarsWrapper>
                {EXAM_TYPE_LABEL.map((_, index) => (
                  <SkeletonBar
                    key={index}
                    variant="rectangular"
                    height={`${(index + 1) * 15 + 25}%`}
                    animation="wave"
                  />
                ))}
              </SkeletonBarsWrapper>
              <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 1 }}>
                {EXAM_TYPE_LABEL.map((_, index) => (
                  <Skeleton
                    key={index}
                    variant="text"
                    width={40}
                    height={20}
                    animation="wave"
                  />
                ))}
              </Box>
            </SkeletonContainer>
          ) : (
            <ResponsiveContainer width="100%" height="100%" className="domain-performance-graph">
              <BarChart
                className="domain-performance-graph-chart"
                data={chartData}
                margin={{ top: 8, right: 8, bottom: 60, left: 0 }}
                barCategoryGap="10%"
                barGap={4}
              >
              <CartesianGrid strokeDasharray="3 3" opacity={0} vertical={false} />
              <XAxis
                dataKey="name"
                axisLine={false}
                interval={0}
                tickMargin={25}
                tickLine={false}
                angle={-45}
                textAnchor="end"
                tickFormatter={(value) =>
                  value && value.length > 6 ? `${value.substring(0, 6)}...` : value
                }
                height={60}
                tick={{ fill: '#61677E', fontSize: 'clamp(10px, 2.5vw, 12px)' }}
              />
              <YAxis
                axisLine={false}
                domain={[0, 100]}
                allowDecimals={false}
                tickLine={false}
                width={35}
                tick={{ fill: '#61677E', fontSize: 'clamp(10px, 2.2vw, 12px)' }}
              />
              <Tooltip cursor={{ fill: 'rgba(0,0,0,0.06)' }} />
              <Bar dataKey="value" fill="#6EE7EB" radius={[8, 8, 0, 0]} maxBarSize={80}>
                <LabelList 
                  dataKey="value" 
                  position="insideTop" 
                  formatter={(value) => `${value}%`}
                  fill="#000"
                  fontSize={12}
                  fontWeight={600}
                  stroke="#000"
                  strokeWidth={0.3}
                />
              </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartWrap>
      </Box>
    </Box>
    </>
  );
};

export default DomainPerformanceGraph;
